// Live session timeline + clock-sync helpers (issue #28).
//
// The host compiles their plan into a TIMELINE — ordered blocks carrying
// structure and timing only (no exercise payloads) — and uploads it on
// live:start. The server runs it authoritatively; every client resolves the
// block's path against its own (possibly substituted) plan tree for display.

import { compileWorkoutPlan, linkedLabel, loopKeyFor } from "./workoutCompiler";
import { firmwareDisplayType } from "./firmwareTypes";
import { isInjectedChangeover, isTransitionNode, resolveSetScheme } from "./planVisuals";
import { isCalorieUnit, isCountedUnit } from "./planNodeUtils";

// Resolve the node at a source-tree path ("children" / index pairs) against the
// plan root — same walk the follower progress code uses. Returns undefined for
// an out-of-range path.
function nodeAtPath(root, path) {
  let node = root;
  for (let j = 0; j < path.length; j += 2) {
    node = node?.[path[j]]?.[path[j + 1]];
  }
  return node;
}

// The set a live block is running: the iteration of the nearest multi-iteration
// Group ancestor on its path, read out of the block's own loopCounters. This is
// the rule workoutCompiler's `walk` applies when it resolves a `setScheme`
// (`enclosingGroup.iter`) — single-iteration Groups are transparent, and the
// deepest repeating ancestor wins. Keyed via the compiler's own loopKeyFor so a
// Group with no id resolves through the `@path` fallback rather than silently
// reading set 1 (issue #74).
export function blockSetIndex(plan, block) {
  const path = block?.path;
  if (!plan || !Array.isArray(path) || path.length === 0) return 0;
  let setIdx = 0;
  let node = plan;
  const walked = [];
  // Stop one pair short of the block's own node — a node is not its own
  // enclosing group.
  for (let i = 0; i < path.length && node; i += 2) {
    if (node.type === "Group" && node.value > 1) {
      setIdx = block.loopCounters?.[loopKeyFor(node, walked)] ?? setIdx;
    }
    walked.push(path[i], path[i + 1]);
    node = node[path[i]]?.[path[i + 1]];
  }
  return setIdx;
}

// The node a live block points at, resolved for the set actually in progress.
//
// The wire timeline carries structure and timing only — no exercise payload —
// so every client re-resolves the block against its OWN plan tree. Resolving
// the path alone yields the raw authored node, whose static value/weight by
// schema rule only mirror set 1: a ramped block then reads "8 reps · 75 kg" on
// every set of an 8/6/5/5 ramp, while the outline beside it (which does resolve
// per set) disagrees. Always go through this rather than a bare path lookup.
export function resolveBlockNode(plan, block) {
  if (!plan || !block?.path) return null;
  const node = nodeAtPath(plan, block.path);
  if (!node) return null;
  const setIdx = blockSetIndex(plan, block);
  const first = resolveSetScheme(node, setIdx);
  // Steps linked to this one ride the same block (#177): the compiler folded
  // them in, so the block is all of them. Rebuild the same view from the plan
  // so the follower shows the joined name and logs each step.
  const parent = nodeAtPath(plan, block.path.slice(0, -2));
  const idx = block.path[block.path.length - 1];
  const counted = (n) => n && n.type !== "Group" && isCountedUnit(n.unit);
  if (!counted(first) || !Array.isArray(parent?.children) || !Number.isInteger(idx)) return first;
  const nodes = [first];
  for (let i = idx + 1; i < parent.children.length; i += 1) {
    const next = parent.children[i];
    if (isTransitionNode(next)) continue; // dropped from a linked run
    if (!next?.linkToPrevious || !counted(next)) break;
    nodes.push(resolveSetScheme(next, setIdx));
  }
  if (nodes.length < 2) return first;
  return {
    ...first,
    label: linkedLabel(nodes),
    linkedNodes: nodes,
    // The block carries only the run's total estimate; weight by count.
    linkedShares: nodes.map((n) => Number(n.value) || 1),
  };
}

// "Which major block of how many" for a block's path — the top-level phase
// stepper the app already draws (MacroProgress block pills). The device's
// bottom-right segment group renders this as `phase_in_round`/`phases_in_round`,
// so a 40-interval session across 7 blocks lights up 7 squares instead of the
// flat interval index (the "22/40" fallback). Mirror of the block computation in
// useWorkoutUiState.js — keep them in sync so the panel matches the app.
//
// Returns { current (1-based), total } or null when there are no distinct
// top-level phases (a single deeply-nested block — caller falls back to the
// repeating-group ring).
// Label of the top-level section a block belongs to (KhronosFirmware#49) — the
// macro overview the device shows in the lobby. Mirrors computeBlockProgress's
// unwrap so the label lines up with phase_in_round.
export function computeSectionLabel(plan, path) {
  let planNode = plan;
  let pathDepth = 0;
  while (
    planNode?.children?.length === 1 &&
    planNode.children[0]?.type === "Group" &&
    planNode.children[0]?.children?.length > 1
  ) {
    planNode = planNode.children[0];
    pathDepth += 2;
  }
  const topChildren = planNode?.children || [];
  if (topChildren.length <= 1) return null;
  const idx = path.length >= pathDepth + 2 ? path[pathDepth + 1] : 0;
  return topChildren[idx]?.label || null;
}

export function computeBlockProgress(plan, path) {
  // Unwrap single-child container Groups to reach the distinct top-level phases
  // (same as the app's macro stepper); track how deep we descended so the path
  // index still points at the active top-level phase.
  let planNode = plan;
  let pathDepth = 0;
  while (
    planNode?.children?.length === 1 &&
    planNode.children[0]?.type === "Group" &&
    planNode.children[0]?.children?.length > 1
  ) {
    planNode = planNode.children[0];
    pathDepth += 2; // skip one "children", idx pair
  }
  const topChildren = planNode?.children || [];
  if (topChildren.length <= 1) return null;
  const topActiveIndex = path.length >= pathDepth + 2 ? path[pathDepth + 1] : 0;

  // Injected changeovers — time-only transitions AND Setup sections — are never
  // shown in the macro stepper (issue #22), so they don't count as blocks. Setup
  // sections ARE executable (they land on the wire timeline), so unlike plain
  // transitions the active index CAN key off a setup; excluding them here keeps
  // the follower's phase squares matching the app's block pills (issue #42).
  // The active index keys off the ORIGINAL child index either way.
  const kept = topChildren
    .map((child, i) => ({ child, i }))
    .filter(({ child }) => !isInjectedChangeover(child));
  let pos = kept.findIndex(({ i }) => i === topActiveIndex);
  // The active block is itself a changeover (a Setup section — executable, so
  // computeBlockProgress runs for it). Point the panel at the upcoming real
  // block ("setting up for block N"); fall back to the last block if the setup
  // trails everything.
  if (pos < 0) {
    if (!kept.length) return null;
    const next = kept.findIndex(({ i }) => i > topActiveIndex);
    pos = next >= 0 ? next : kept.length - 1;
  }
  return { current: pos + 1, total: kept.length };
}

// Build the wire timeline from a workout plan. Mirrors the solo engine's
// compiled queue but keeps only executable items (transitions are skipped by
// the engine and have no place on a shared clock).
export function buildLiveTimeline(plan, { profile, historyMap, skipLastRest = true } = {}) {
  const { queue } = compileWorkoutPlan(plan, { skipLastRest, profile, historyMap });
  return queue
    .filter((item) => item.executable !== false)
    .map((item) => {
      // A clock with no length (Stopwatch, For Time, Free training) runs until
      // you stop it. As a 0 s countdown the runner ended it after a second;
      // the open "reps" mode waits for Next instead, with no count to show.
      const openEnded = item.isTimed && !(item.durationSec > 0);
      const block = {
        key: `${item.sourceTreePath.join(",")}#${JSON.stringify(item.loopCounters)}`,
        path: [...item.sourceTreePath],
        loopCounters: { ...item.loopCounters },
        // Wire "mode": "timed" counts down, "reps" waits for the Next press.
        // Calorie blocks are self-paced too, so they ride the "reps" mode and
        // carry their target in `cal` (see below) — a new mode string would be
        // an unknown value to firmware already in the field.
        type: item.isTimed && !openEnded ? "timed" : "reps",
        kind: item.kind === "recovery" ? "recovery" : "active",
        durationSec: Math.max(1, Math.round(item.durationSec || 0)),
      };
      // Open-ended: it runs until the host says Next, never on a rep cap or
      // everyone's Done, so a shared Free training isn't cut short (#199).
      if (openEnded) block.open = true;
      // Activity name + firmware phase type for hardware followers. The device
      // holds no plan tree, so without these it renders the generic phase type
      // ("Work") instead of the exercise name and can't colour warm/cool/round-
      // rest phases. The server forwards them verbatim (truncating the label);
      // see api/sockets/live.js deviceStateProjection. displayType needs the
      // parent Group + index to detect a structural round-rest (RndRest).
      if (item.node?.label) block.label = item.node.label;
      const parent = nodeAtPath(plan, item.sourceTreePath.slice(0, -2));
      const displayType = firmwareDisplayType(
        item.node,
        parent,
        item.sourceTreePath[item.sourceTreePath.length - 1],
      );
      if (displayType) block.displayType = displayType;
      // Round/block indicators for hardware followers (issue #38). The device
      // holds no plan tree, so we precompute them here. round/rounds is the
      // innermost multi-iteration Group ring (deepest-first from
      // computeLiveProgress; single-iteration wrappers with total <= 1 are not
      // rounds). Omitted → the device hides that indicator.
      const rings = computeLiveProgress(plan, block).filter((r) => r.total > 1);
      if (rings[0]) {
        block.round = rings[0].current; // 1-based round within the block
        block.rounds = rings[0].total;
      }
      // phase_in_round/phases_in_round: the second-lowest repeating level, so
      // the panel's two square rows show the two levels that change most often
      // (KhronosFirmware#87): 3 blocks of 4 rounds of an 8-set Tabata shows the
      // round (4) and the set (8), not the blocks, which barely change. Only
      // with one repeating level does the other row fall back to "which major
      // block of how many" (the app's block pills).
      const blockProgress = computeBlockProgress(plan, block.path);
      if (rings[1]) {
        block.phaseInRound = rings[1].current;
        block.phasesInRound = rings[1].total;
      } else if (blockProgress) {
        block.phaseInRound = blockProgress.current;
        block.phasesInRound = blockProgress.total;
      }
      // Top-level section name for the device's lobby macro overview (#49).
      const sectionLabel = computeSectionLabel(plan, block.path);
      if (sectionLabel) block.sectionLabel = sectionLabel;
      // Counted target for hardware followers (KhronosFirmware#10). These blocks
      // carry only an *estimated* durationSec; the device needs the actual count
      // to show "x10" and wait for the Next press instead of rendering a
      // countdown. Calories go in their own field rather than `reps` — a
      // 100 cal bike sprint rendered as "x100" reads as a hundred repetitions.
      // Firmware that predates the field shows the bare label, which is honest;
      // reusing `reps` would not be.
      if (block.type === "reps" && !openEnded) {
        const count = Number(item.node?.value);
        if (Number.isFinite(count) && count > 0) {
          const field = isCalorieUnit(item.node?.unit) ? "cal" : "reps";
          block[field] = Math.round(count);
        }
      }
      return block;
    });
}

// NTP-style offset from a [t0 client-send, t1 server-recv, t2 server-send,
// t3 client-recv] sample: strips symmetric transport latency.
export function ntpOffsetFromSample({ t0, t1, t2, t3 }) {
  return ((t1 - t0) + (t2 - t3)) / 2;
}

export function ntpRttFromSample({ t0, t1, t2, t3 }) {
  return (t3 - t0) - (t2 - t1);
}

// Pick the offset from the lowest-RTT sample — least queueing noise, closest
// to the true symmetric-latency assumption.
export function bestNtpOffset(samples) {
  if (!samples?.length) return 0;
  let best = samples[0];
  let bestRtt = ntpRttFromSample(best);
  for (const s of samples.slice(1)) {
    const rtt = ntpRttFromSample(s);
    if (rtt < bestRtt) {
      best = s;
      bestRtt = rtt;
    }
  }
  return ntpOffsetFromSample(best);
}

// Derive the cosmetic countdown values for a snapshot at a given
// offset-corrected client time. Pure so it is trivially testable; the
// follower hook calls this on a 250 ms interval.
export function deriveCountdowns(snapshot, nowServerMs) {
  if (!snapshot) {
    return { countInRemaining: 0, timeLeftInPhase: 0, repCapRemaining: null };
  }
  const { status, phaseStartsAt, phaseEndsAt, repCapEndsAt, timeRemainingMs, block } = snapshot;

  if (status === "paused") {
    return {
      countInRemaining: 0,
      timeLeftInPhase: Math.ceil((timeRemainingMs ?? 0) / 1000),
      repCapRemaining: block?.type === "reps" && timeRemainingMs != null
        ? Math.ceil(timeRemainingMs / 1000)
        : null,
    };
  }

  const countInRemaining =
    status === "waiting_for_sync" && phaseStartsAt
      ? Math.max(0, Math.ceil((phaseStartsAt - nowServerMs) / 1000))
      : 0;

  // During the GET READY window the phase hasn't started — show its full
  // duration; once active, count down against the absolute deadline.
  const timeLeftInPhase = phaseEndsAt
    ? status === "waiting_for_sync" && phaseStartsAt
      ? Math.ceil((phaseEndsAt - phaseStartsAt) / 1000)
      : Math.max(0, Math.ceil((phaseEndsAt - nowServerMs) / 1000))
    : 0;

  const repCapRemaining = repCapEndsAt
    ? Math.max(0, Math.ceil((repCapEndsAt - nowServerMs) / 1000))
    : null;

  return { countInRemaining, timeLeftInPhase, repCapRemaining };
}

// Where a running solo session sits in a freshly compiled live timeline.
//
// Going live mid-workout (#81) has to answer one question: which timeline
// entry is the person standing in? Both sides come out of compileWorkoutPlan,
// and profile/historyMap only feed rep DURATION estimates — never which items
// are emitted or their order — so with the same skipLastRest the two queues
// are structurally identical. They are not index-identical, though:
// buildLiveTimeline drops non-executable transitions that the engine keeps in
// its queue. So the position travels as an identity, not a number.
//
// Compared field-wise rather than by the timeline's own `key` string, which is
// a JSON.stringify of loopCounters and would therefore depend on key insertion
// order — true today, and a silent wrong-block bug the day it isn't.
// Plan order for two tree paths. Element-wise, and numeric where both sides
// are numbers: paths alternate "children" with an index, so a string compare
// would put child 10 before child 2.
const comparePaths = (a = [], b = []) => {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i += 1) {
    const x = a[i];
    const y = b[i];
    if (typeof x === "number" && typeof y === "number") {
      if (x !== y) return x < y ? -1 : 1;
    } else if (String(x) !== String(y)) {
      return String(x) < String(y) ? -1 : 1;
    }
  }
  if (a.length === b.length) return 0;
  return a.length < b.length ? -1 : 1;
};

const samePosition = (block, position) => {
  const a = block?.path || [];
  const b = position?.path || [];
  if (a.length !== b.length) return false;
  if (!a.every((seg, i) => String(seg) === String(b[i]))) return false;

  const ac = block?.loopCounters || {};
  const bc = position?.loopCounters || {};
  const keys = Object.keys(ac);
  if (keys.length !== Object.keys(bc).length) return false;
  return keys.every((k) => Number(ac[k]) === Number(bc[k]));
};

/**
 * Index of `position` ({ path, loopCounters }) in a compiled live timeline.
 * Returns 0 when it cannot be placed: a handover that lands on the warm-up is
 * wrong, but a handover that refuses to start is worse.
 */
export function findTimelineIndexFor(timeline, position) {
  if (!Array.isArray(timeline) || !position?.path?.length) return 0;
  const exact = timeline.findIndex((block) => samePosition(block, position));
  if (exact >= 0) return exact;
  // Defensive: the two compiles disagreed (a different skipLastRest, say).
  // Fall back to the first entry at or after the position in plan order, so a
  // handover resumes forwards rather than repeating work already done.
  const after = timeline.findIndex(
    (block) => comparePaths(block.path, position.path) >= 0,
  );
  return after >= 0 ? after : 0;
}

// Concentric-ring round progress for a live block: walk the plan along the
// block's path collecting repeating Group ancestors, deepest first. A lean
// mirror of useWorkoutCoach's computeProgressItems for follower rendering.
export function computeLiveProgress(plan, block) {
  if (!plan || !block?.path?.length) return [];
  const items = [];
  const tempPath = [];
  for (let i = 0; i < block.path.length; i += 2) {
    tempPath.push(block.path[i], block.path[i + 1]);
    const parentPath = tempPath.slice(0, -2);
    let parentNode = plan;
    for (let j = 0; j < parentPath.length; j += 2) {
      parentNode = parentNode?.[parentPath[j]]?.[parentPath[j + 1]];
    }
    if (parentNode?.type === "Group") {
      items.unshift({
        label: parentNode.label || parentNode.unit || "Items",
        // Keyed the compiler's way, so an id-less Group reads its `@path`
        // counter instead of reporting round 1 forever (issue #74).
        current:
          (block.loopCounters?.[loopKeyFor(parentNode, parentPath)] || 0) + 1,
        total: parentNode.value,
        type: "group",
      });
    }
  }
  return items;
}

// ── Mid-session edits (#175) ─────────────────────────────────────────────────

// Ids of the nodes a block passes through, root excluded: the step itself and
// every block it sits in.
function idsAlongPath(plan, path) {
  const ids = [];
  let node = plan;
  for (let j = 0; j < path.length && node; j += 2) {
    node = node?.[path[j]]?.[path[j + 1]];
    if (node?.id != null) ids.push(node.id);
  }
  return ids;
}

/**
 * Steps (and blocks) already finished at `position`: every node that ran
 * before it and has nothing left to run. A step inside a five-round block is
 * not finished after round one, so it stays editable until its last round.
 * Positions are matched as findTimelineIndexFor does.
 * @returns {Set<string>}
 */
export function finishedNodeIds(plan, position, options = {}) {
  const done = new Set();
  if (!plan || !position?.path?.length) return done;
  const timeline = buildLiveTimeline(plan, options);
  const current = findTimelineIndexFor(timeline, position);
  const pending = new Set();
  timeline.forEach((block, i) => {
    const target = i < current ? done : pending;
    idsAlongPath(plan, block.path).forEach((id) => target.add(id));
  });
  pending.forEach((id) => done.delete(id));
  return done;
}

const sameCounters = (a = {}, b = {}) => {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => Number(a[k]) === Number(b[k]));
};

/**
 * Recompile an edited plan for a running live session and work out where the
 * person now stands in it.
 *
 * Paths are positional, so deleting a finished step shifts every path after
 * it; node ids survive edits (planEditing preserves them), so the block in
 * progress is found again by id and round. If it was deleted, the next block
 * that survived is where they pick up — never the warm-up. `sameBlock` tells
 * the server whether to keep the time already served.
 *
 * @returns {{ timeline: object[], atIndex: number, sameBlock: boolean }}
 */
export function rebaseLiveEdit(prevPlan, nextPlan, currentBlock, options = {}) {
  const timeline = buildLiveTimeline(nextPlan, options);
  const prevTimeline = buildLiveTimeline(prevPlan, options);
  const from = findTimelineIndexFor(prevTimeline, currentBlock);

  const identity = (plan, block) => nodeAtPath(plan, block.path)?.id ?? null;
  for (let i = from; i < prevTimeline.length; i += 1) {
    const prev = prevTimeline[i];
    const id = identity(prevPlan, prev);
    const j =
      id == null
        ? timeline.findIndex((b) => samePosition(b, prev))
        : timeline.findIndex(
            (b) =>
              identity(nextPlan, b) === id &&
              sameCounters(b.loopCounters, prev.loopCounters),
          );
    if (j >= 0) return { timeline, atIndex: j, sameBlock: i === from };
  }
  // Everything from here on was deleted: stand on the last block left.
  return { timeline, atIndex: Math.max(0, timeline.length - 1), sameBlock: false };
}
